import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    usePostJournalEntryMutation,
    useSaveJournalDraftMutation,
} from '@/api/queries/journal';
import { usePeriodStatusQuery } from '@/api/queries/periods';
import { useChartOfAccounts } from '@/api/queries';
import { computeValidationState } from '@/api/handlers/resources/journal';
import type { JournalEntry, LocaleCode } from '@/api/models';
import { formatGel } from '@/pages/accounting/books/chartOfAccounts/utils';
import {
    postBlockedReason,
    postErrorMessage,
    saveErrorMessage,
    type AccountOption,
} from './journalEntryMeta';
import {
    fromEntry,
    hasSavableContent,
    makeBlankLine,
    makeInitialState,
    newEntryId,
    parseLine,
    toValidationInput,
    toWriteRequest,
    type JournalFormState,
    type LineParse,
} from './utils';
import { useResetOnKeyChange } from '@/utils';

const SUPPORTED_LOCALES: LocaleCode[] = ['ka', 'en'];

const resolveLocale = (language: string): LocaleCode =>
    (SUPPORTED_LOCALES as string[]).includes(language)
        ? (language as LocaleCode)
        : 'en';

type UseJournalEntryFormParams = {
    /** An existing draft to edit; omit/`null` for a fresh entry. */
    entry?: JournalEntry | null;
    /** Fired after a successful save or post, with the server's entry. */
    onSaved?: (entry: JournalEntry) => void;
};

export type PostFeedback = {
    kind: 'saved' | 'posted';
    /** Assigned number when posted; `null` = pending (assigned on sync). */
    entryNumber: string | null;
};

export const useJournalEntryForm = ({
    entry = null,
    onSaved,
}: UseJournalEntryFormParams = {}) => {
    const { t, i18n } = useTranslation('pages');
    const locale = resolveLocale(i18n.language);

    // Stable row keys, independent of array position, so focus survives edits.
    const keyRef = React.useRef(0);
    const makeKey = React.useCallback(() => `line-${keyRef.current++}`, []);

    // Client id for idempotent, offline-first create/save.
    const idRef = React.useRef(entry?.id ?? newEntryId());
    /**
     * Last etag seen for this entry, or `''` when it has never been saved.
     *
     * This is what selects create from update: empty means `POST` (create,
     * idempotent on the client id), non-empty means `PUT` guarded by the token.
     * Getting it wrong is silent rather than loud — create is *first-write-wins*,
     * so saving an edited draft down the create path returns 200 and keeps the
     * original content, losing the edit with no error anywhere.
     *
     * `JournalService` re-reads after a create, so what comes back here is
     * always an etag the server will accept on the next write.
     */
    const etagRef = React.useRef(entry?.etag ?? '');
    const [state, setState] = React.useState<JournalFormState>(() =>
        entry ? fromEntry(entry, makeKey) : makeInitialState(makeKey),
    );
    const [feedback, setFeedback] = React.useState<PostFeedback | null>(null);
    const [saveError, setSaveError] = React.useState<string | null>(null);

    const isEditingPosted = entry?.status === 'POSTED';

    // Re-hydrate when the edited entry changes identity — strictly on that
    // transition, so a background refetch of the same entry does not throw away
    // what the user has typed into it.
    const editedId = entry?.id ?? null;
    useResetOnKeyChange(editedId, () => {
        idRef.current = entry?.id ?? newEntryId();
        etagRef.current = entry?.etag ?? '';
        setState(entry ? fromEntry(entry, makeKey) : makeInitialState(makeKey));
        setFeedback(null);
        setSaveError(null);
    });

    // --- Accounts (Task 01 query, filtered to live postable leaves) ---------
    //
    // Both checks are needed. The chart is fetched with archived rows included
    // (the archive gate, the parent picker and reactivation all read them), and
    // `isPostable` is structural — "a leaf, not a group" — so it stays true for
    // an archived leaf on purpose: the statements need archived leaves that
    // still carry a balance. Offering one here would only earn an
    // `ACCOUNT_ARCHIVED` at save. Matches the fixed-asset pickers.
    const {
        data: accounts,
        isLoading: accountsLoading,
        error: accountsError,
    } = useChartOfAccounts({ locale });

    const options = React.useMemo<AccountOption[]>(() => {
        if (!accounts) {
            return [];
        }
        return accounts
            .filter((account) => account.isPostable && !account.archived)
            .map((account) => {
                const name = account.name;
                return {
                    id: account.id,
                    code: account.code,
                    name,
                    searchText: `${account.code} ${name}`,
                };
            })
            .sort((a, b) => a.code.localeCompare(b.code));
    }, [accounts]);

    const optionById = React.useMemo(() => {
        const map = new Map<string, AccountOption>();
        options.forEach((option) => map.set(option.id, option));
        return map;
    }, [options]);

    // --- Period status for the selected date --------------------------------
    const periodQuery = usePeriodStatusQuery(state.date);
    const periodResponse = periodQuery.data;
    const periodStatus = periodResponse?.success
        ? periodResponse.response
        : null;
    const periodClosed = periodStatus?.state === 'CLOSED';
    const periodResolved = periodStatus !== null;

    // --- Derived validity (live, in tetri) ----------------------------------
    const lineParses = React.useMemo<LineParse[]>(
        () => state.lines.map(parseLine),
        [state.lines],
    );
    const hasParseErrors = lineParses.some(
        (parse) => parse.debitError !== null || parse.creditError !== null,
    );

    const validationState = React.useMemo(
        () =>
            computeValidationState(state.lines.map(toValidationInput), {
                periodClosed,
            }),
        [state.lines, periodClosed],
    );

    const totalDebit = formatGel(validationState.totalDebitTetri);
    const totalCredit = formatGel(validationState.totalCreditTetri);
    const differenceGel = formatGel(Math.abs(validationState.differenceTetri));
    const hasAmounts =
        validationState.totalDebitTetri > 0 ||
        validationState.totalCreditTetri > 0;
    const balanced = validationState.differenceTetri === 0 && hasAmounts;

    const saveMutation = useSaveJournalDraftMutation();
    const postMutation = usePostJournalEntryMutation();
    const isSaving = saveMutation.isPending;
    const isPosting = postMutation.isPending;
    const isBusy = isSaving || isPosting;

    // A draft can be saved with an incomplete/unbalanced entry, but NOT with an
    // unparseable amount — the write edge would silently coerce it to 0 and the
    // user's input would be lost on reload. The offending field shows its own
    // error, so blocking here (rather than hinting) is the safe choice.
    const canSaveDraft =
        hasSavableContent(state) &&
        !hasParseErrors &&
        !isBusy &&
        !isEditingPosted;
    const canPost =
        validationState.isPostable &&
        !hasParseErrors &&
        periodResolved &&
        !isBusy &&
        !isEditingPosted;

    const postReason = canPost
        ? null
        : hasParseErrors
          ? t('journalEntry.reasons.fixAmounts')
          : postBlockedReason(validationState, t);

    // --- Mutators -----------------------------------------------------------
    const patch = React.useCallback(
        (update: (prev: JournalFormState) => JournalFormState) => {
            setFeedback(null);
            setSaveError(null);
            setState(update);
        },
        [],
    );

    const updateLine = React.useCallback(
        (
            key: string,
            update: (
                line: JournalFormState['lines'][number],
            ) => JournalFormState['lines'][number],
        ) =>
            patch((prev) => ({
                ...prev,
                lines: prev.lines.map((line) =>
                    line.key === key ? update(line) : line,
                ),
            })),
        [patch],
    );

    const onDateChange = React.useCallback(
        (date: string) => patch((prev) => ({ ...prev, date })),
        [patch],
    );
    const onDescriptionChange = React.useCallback(
        (description: string) => patch((prev) => ({ ...prev, description })),
        [patch],
    );
    const onDocumentRefChange = React.useCallback(
        (documentRef: string) => patch((prev) => ({ ...prev, documentRef })),
        [patch],
    );

    const onLineAccountChange = React.useCallback(
        (key: string, accountId: string | null) =>
            updateLine(key, (line) => ({ ...line, accountId })),
        [updateLine],
    );
    // One side per line: typing a debit clears the credit, and vice versa.
    const onLineDebitChange = React.useCallback(
        (key: string, debit: string) =>
            updateLine(key, (line) => ({
                ...line,
                debit,
                credit: debit.trim() !== '' ? '' : line.credit,
            })),
        [updateLine],
    );
    const onLineCreditChange = React.useCallback(
        (key: string, credit: string) =>
            updateLine(key, (line) => ({
                ...line,
                credit,
                debit: credit.trim() !== '' ? '' : line.debit,
            })),
        [updateLine],
    );
    const onLineMemoChange = React.useCallback(
        (key: string, memo: string) =>
            updateLine(key, (line) => ({ ...line, memo })),
        [updateLine],
    );

    const onAddLine = React.useCallback(
        () =>
            patch((prev) => ({
                ...prev,
                lines: [...prev.lines, makeBlankLine(makeKey())],
            })),
        [makeKey, patch],
    );
    const onRemoveLine = React.useCallback(
        (key: string) =>
            patch((prev) =>
                prev.lines.length <= 1
                    ? prev // keep at least one row
                    : {
                          ...prev,
                          lines: prev.lines.filter((line) => line.key !== key),
                      },
            ),
        [patch],
    );
    // Enter in the last row adds a new line (keyboard-first batch entry).
    const onRowEnter = React.useCallback(
        (key: string) => {
            if (state.lines.at(-1)?.key === key) {
                onAddLine();
            }
        },
        [onAddLine, state.lines],
    );

    const resetForNext = React.useCallback(() => {
        idRef.current = newEntryId();
        // A fresh id is a fresh entry: carrying the old token over would send
        // the next save down the update path against an entry that is gone.
        etagRef.current = '';
        keyRef.current = 0;
        setState((prev) => ({
            ...makeInitialState(makeKey),
            date: prev.date, // keep the date for same-day batch entry
        }));
    }, [makeKey]);

    const onStartAnother = React.useCallback(() => {
        setFeedback(null);
        setSaveError(null);
        resetForNext();
    }, [resetForNext]);

    const onSaveDraft = React.useCallback(async () => {
        if (!canSaveDraft) {
            return;
        }
        setSaveError(null);
        const result = await saveMutation.mutateAsync({
            body: toWriteRequest(idRef.current, state),
            etag: etagRef.current || undefined,
        });
        if (!result.success) {
            setSaveError(saveErrorMessage(result.error, t));
            return;
        }
        etagRef.current = result.response.etag;
        setFeedback({ kind: 'saved', entryNumber: null });
        onSaved?.(result.response);
    }, [canSaveDraft, onSaved, saveMutation, state, t]);

    const onPost = React.useCallback(async () => {
        if (!canPost) {
            return;
        }
        setSaveError(null);
        const result = await postMutation.mutateAsync({
            body: toWriteRequest(idRef.current, state),
            etag: etagRef.current || undefined,
        });
        if (!result.success) {
            // The save leg may have succeeded before the post was refused, in
            // which case the entry exists and its token has moved on. Drop ours
            // so the next attempt re-creates rather than updating with a token
            // we can no longer vouch for.
            etagRef.current = '';
            setSaveError(postErrorMessage(result.error, t));
            return;
        }
        setFeedback({
            kind: 'posted',
            entryNumber: result.response.entryNumber,
        });
        onSaved?.(result.response);
        resetForNext();
    }, [canPost, onSaved, postMutation, resetForNext, state, t]);

    return {
        // identity / mode
        mode: entry ? ('edit' as const) : ('create' as const),
        isEditingPosted,
        // header
        date: state.date,
        description: state.description,
        documentRef: state.documentRef,
        onDateChange,
        onDescriptionChange,
        onDocumentRefChange,
        // lines
        lines: state.lines,
        lineParses,
        options,
        optionById,
        accountsLoading,
        accountsError: accountsError ?? null,
        onLineAccountChange,
        onLineDebitChange,
        onLineCreditChange,
        onLineMemoChange,
        onAddLine,
        onRemoveLine,
        onRowEnter,
        // totals / validity
        validationState,
        totalDebit,
        totalCredit,
        differenceGel,
        balanced,
        hasAmounts,
        // period
        periodStatus,
        periodClosed,
        periodResolved,
        // actions
        canSaveDraft,
        canPost,
        postReason,
        isSaving,
        isPosting,
        isBusy,
        onSaveDraft,
        onPost,
        // feedback
        feedback,
        saveError,
        onStartAnother,
        locale,
    };
};

export type JournalEntryFormModel = ReturnType<typeof useJournalEntryForm>;
